import type { LoginSecondFactor, LoginSso } from '@pos/admin-ui';
import type { ApiProblem } from '@pos/api-client';
import { problemText } from '@pos/i18n';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { hoApi, TENANT_CODE, useSession } from '../api';

const failed = (problem: ApiProblem | undefined, status: number) =>
  problemText(problem?.code ?? (status === 429 ? 'too_many_requests' : undefined), problem?.title);

/**
 * HO sign-in (ADR 0004, ADR 0121): staff code and password, then the authenticator app's code when two-step sign-in is on; or the
 * company's identity provider, which comes back to the portal with a one-time code (`?sso=`) exchanged here for a session, or with
 * the reason it failed (`?sso_error=`). Either is taken out of the address at once.
 */
/** The provider's answer in the portal's address, taken out of it at once so a reload or a shared link can't reuse it. */
function takeProviderAnswer() {
  const params = new URLSearchParams(window.location.search);
  const answer = { code: params.get('sso'), error: params.get('sso_error') };
  if (answer.code || answer.error) {
    params.delete('sso');
    params.delete('sso_error');
    const rest = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash);
  }
  return answer;
}

export function useHoLogin() {
  const [pending, setPending] = useState<{ staffCode: string; password: string }>();
  const [answer] = useState(takeProviderAnswer);
  const [notice, setNotice] = useState(() => (answer.error ? problemText(answer.error, undefined) : undefined));

  const sso = useQuery({
    queryKey: ['ho', 'sso-info', TENANT_CODE],
    queryFn: async () => {
      const { data } = await hoApi.GET('/api/v1/identity/sso/{tenantCode}', {
        params: { path: { tenantCode: TENANT_CODE } },
      });
      return data ?? { enabled: false, displayName: null };
    },
    staleTime: Infinity,
  });

  useEffect(() => {
    const code = answer.code;
    if (!code) return;
    void hoApi.POST('/api/v1/identity/sso/exchange', { body: { code } }).then(({ data, error, response }) => {
      if (data) useSession.getState().start(data);
      else setNotice(failed(error as ApiProblem | undefined, response.status));
    });
  }, [answer]);

  const signIn = useCallback(async (staffCode: string, password: string, code?: string) => {
    const { data, error, response } = await hoApi.POST('/api/v1/identity/sessions', {
      body: { tenantCode: TENANT_CODE, staffCode, password, code },
    });
    if (data) {
      setPending(undefined);
      useSession.getState().start(data);
      return undefined;
    }
    const problem = error as ApiProblem | undefined;
    if (problem?.code === 'identity.mfa_code_required') {
      setPending({ staffCode, password });
      return undefined;
    }
    return failed(problem, response.status);
  }, []);

  const secondFactor: LoginSecondFactor | undefined = pending && {
    onSubmit: (code) => signIn(pending.staffCode, pending.password, code),
    onCancel: () => setPending(undefined),
  };
  const provider: LoginSso | undefined = sso.data?.enabled
    ? {
        label: sso.data.displayName ?? '',
        onStart: () =>
          window.location.assign(`/api/v1/identity/sso/start?tenantCode=${encodeURIComponent(TENANT_CODE)}`),
      }
    : undefined;

  return {
    login: (staffCode: string, password: string) => signIn(staffCode, password),
    secondFactor,
    sso: provider,
    notice,
  };
}
